import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '@heroui/react';
import { Badge, Button, Card, EmptyState, Field, HeroSelectField, Input, LoadingScreen, PageHeader } from '../../components/Ui';
import { useToast } from '../../contexts/ToastContext';
import {
  useBusinessBranches,
  useBusinessMenuData,
  useBulkUpsertInventoryAssignments,
  useCreateInventoryTemplate,
  useDeleteInventoryAssignment,
  useDuplicateInventoryTemplate,
  useGenerateInventoryTemplate,
  useInventoryAssignments,
  useInventoryTemplates,
  useUpdateInventoryTemplateItems,
  useUpsertInventoryAssignment,
} from '../../hooks/businessData';
import type { InventoryTemplate } from '../../../shared/types';
import { InventoryTabs } from './InventoryTabs';

const days = [
  { id: 1, label: 'Lun' },
  { id: 2, label: 'Mar' },
  { id: 3, label: 'Mié' },
  { id: 4, label: 'Jue' },
  { id: 5, label: 'Vie' },
  { id: 6, label: 'Sáb' },
  { id: 7, label: 'Dom' },
];

type MenuProductOption = { id: string; name: string; active: boolean };

function templateLabel(template?: InventoryTemplate) {
  if (!template) return 'Sin plantilla';
  return `${template.name} · ${template.items.length} productos`;
}

export default function InventoryTemplatesPage() {
  const { show } = useToast();
  const { data: templates = [], isLoading: templatesLoading } = useInventoryTemplates();
  const { data: assignments = [], isLoading: assignmentsLoading } = useInventoryAssignments();
  const { data: branches = [] } = useBusinessBranches();
  const { data: menuData } = useBusinessMenuData();
  const products = useMemo(() => ((menuData?.products ?? []) as MenuProductOption[]).filter((product) => product.active), [menuData]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [newItemProductId, setNewItemProductId] = useState('');
  const [newItemStock, setNewItemStock] = useState('0');
  const [generateBranchId, setGenerateBranchId] = useState('');
  const [generateDate, setGenerateDate] = useState('');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [bulkBranchIds, setBulkBranchIds] = useState<string[]>([]);
  const [bulkDayIds, setBulkDayIds] = useState<number[]>([]);
  const createTemplate = useCreateInventoryTemplate();
  const duplicateTemplate = useDuplicateInventoryTemplate();
  const updateItems = useUpdateInventoryTemplateItems();
  const generateTemplate = useGenerateInventoryTemplate();
  const bulkUpsertAssignments = useBulkUpsertInventoryAssignments();
  const upsertAssignment = useUpsertInventoryAssignment();
  const deleteAssignment = useDeleteInventoryAssignment();

  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId) ?? templates[0];
  const previousSelectedTemplateId = useRef<string | undefined>(undefined);
  const assignmentByBranchDay = useMemo(() => {
    const map = new Map<string, string>();
    for (const assignment of assignments) map.set(`${assignment.branchId}:${assignment.dayOfWeek}`, assignment.id);
    return map;
  }, [assignments]);

  useEffect(() => {
    if (!selectedTemplate?.id) return;
    setBulkTemplateId((current) => (!current || current === previousSelectedTemplateId.current ? selectedTemplate.id : current));
    previousSelectedTemplateId.current = selectedTemplate.id;
  }, [selectedTemplate?.id]);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const result = await createTemplate.mutateAsync({ name: templateName, type: 'manual', items: [] });
      setSelectedTemplateId(result.templateId);
      setTemplateName('');
      show('Plantilla creada.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo crear la plantilla.', 'error');
    }
  };

  const addItem = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedTemplate || !newItemProductId) return;
    const next = selectedTemplate.items.filter((item) => item.productId !== newItemProductId);
    next.push({ id: crypto.randomUUID(), productId: newItemProductId, productName: '', stockQuantity: Number(newItemStock || 0) });
    try {
      await updateItems.mutateAsync({ templateId: selectedTemplate.id, items: next.map((item) => ({ productId: item.productId, stockQuantity: item.stockQuantity })) });
      setNewItemProductId('');
      setNewItemStock('0');
      show('Producto agregado a la plantilla.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar la plantilla.', 'error');
    }
  };

  const updateItemStock = async (productId: string, value: string) => {
    if (!selectedTemplate) return;
    const items = selectedTemplate.items.map((item) => ({ productId: item.productId, stockQuantity: item.productId === productId ? Number(value || 0) : item.stockQuantity }));
    try {
      await updateItems.mutateAsync({ templateId: selectedTemplate.id, items });
      show('Stock actualizado.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar el stock.', 'error');
    }
  };

  const assign = async (branchId: string, dayOfWeek: number, templateId: string) => {
    try {
      const currentAssignmentId = assignmentByBranchDay.get(`${branchId}:${dayOfWeek}`);
      if (!templateId) {
        if (currentAssignmentId) await deleteAssignment.mutateAsync(currentAssignmentId);
      } else {
        await upsertAssignment.mutateAsync({ branchId, dayOfWeek, templateId });
      }
      show('Calendario actualizado.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar el calendario.', 'error');
    }
  };

  const toggleBulkBranch = (branchId: string) => {
    setBulkBranchIds((current) => current.includes(branchId) ? current.filter((id) => id !== branchId) : [...current, branchId]);
  };

  const toggleBulkDay = (dayId: number) => {
    setBulkDayIds((current) => current.includes(dayId) ? current.filter((id) => id !== dayId) : [...current, dayId]);
  };

  const selectBulkDays = (dayIds: number[]) => setBulkDayIds(dayIds);

  const applyBulkAssignments = async () => {
    if (!bulkTemplateId || bulkBranchIds.length === 0 || bulkDayIds.length === 0) return;
    try {
      await bulkUpsertAssignments.mutateAsync({ templateId: bulkTemplateId, branchIds: bulkBranchIds, dayOfWeeks: bulkDayIds });
      show('Calendario actualizado.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudieron actualizar las asignaciones.', 'error');
    }
  };

  const generate = async () => {
    if (!selectedTemplate || !generateBranchId) return;
    try {
      const result = await generateTemplate.mutateAsync({ templateId: selectedTemplate.id, branchId: generateBranchId, date: generateDate });
      show(`Plantilla generada con ${result.itemsGenerated} productos.`, 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo generar la plantilla.', 'error');
    }
  };

  const duplicate = async () => {
    if (!selectedTemplate) return;
    try {
      const result = await duplicateTemplate.mutateAsync({ templateId: selectedTemplate.id });
      setSelectedTemplateId(result.templateId);
      show('Plantilla duplicada.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo duplicar la plantilla.', 'error');
    }
  };

  if (templatesLoading || assignmentsLoading) return <LoadingScreen label="Cargando inventario…" />;

  return (
    <div>
      <PageHeader
        title="Plantillas de inventario"
        description="Definí stock diario por producto y asignalo por sucursal y día."
      />
      <InventoryTabs />
      <div className="grid gap-5 xl:grid-cols-[320px_1fr]">
        <Card className="grid gap-4 self-start">
          <form onSubmit={create} className="grid gap-3">
            <Field label="Nueva plantilla">
              <Input value={templateName} onChange={(event) => setTemplateName(event.target.value)} placeholder="Ej. Semanal BBQ" />
            </Field>
            <Button type="submit" busy={createTemplate.isPending} disabled={!templateName.trim()}>Crear plantilla</Button>
          </form>
          <div className="grid gap-2">
            {templates.length === 0 ? <EmptyState title="Sin plantillas" description="Creá la primera plantilla para empezar." /> : templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => setSelectedTemplateId(template.id)}
                className={`rounded-2xl border p-3 text-left text-sm transition ${selectedTemplate?.id === template.id ? 'border-[var(--app-brand-red)] bg-red-50' : 'border-[var(--app-brand-line)] bg-white'}`}
              >
                <div className="font-black text-[var(--app-brand-ink)]">{template.name}</div>
                <div className="mt-1 text-xs text-[var(--app-brand-muted)]">{template.items.length} productos · {template.type === 'auto' ? 'Auto' : 'Manual'}</div>
              </button>
            ))}
          </div>
        </Card>

        {selectedTemplate ? (
          <div className="grid gap-5">
            <Card>
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-black text-[var(--app-brand-ink)]">{selectedTemplate.name}</h2>
                  <Badge tone={selectedTemplate.type === 'auto' ? 'blue' : 'slate'}>{selectedTemplate.type === 'auto' ? 'Generada' : 'Manual'}</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  <HeroSelectField className="min-w-52" value={generateBranchId || null} onChange={(value) => setGenerateBranchId(String(value ?? ''))} options={[{ id: '', label: 'Sucursal para generar' }, ...branches.map((branch) => ({ id: branch.id, label: branch.name }))]} />
                  <Input className="w-40" type="date" value={generateDate} onChange={(event) => setGenerateDate(event.target.value)} />
                  <Button variant="secondary" busy={generateTemplate.isPending} disabled={!generateBranchId} onPress={generate}>Generar</Button>
                  <Button variant="secondary" busy={duplicateTemplate.isPending} onPress={duplicate}>Duplicar plantilla</Button>
                  <Modal>
                    <Button variant="secondary">Abrir calendario semanal</Button>
                    <Modal.Backdrop>
                      <Modal.Container size="cover" scroll="inside">
                        <Modal.Dialog className="max-h-[85vh]">
                          <Modal.CloseTrigger />
                          <Modal.Header>
                            <Modal.Heading>Calendario semanal</Modal.Heading>
                            <p className="text-sm text-[var(--app-brand-muted)]">Asigná plantillas por sucursal y día.</p>
                          </Modal.Header>
                          <Modal.Body>
                            <section className="mb-5 grid gap-4 rounded-2xl border border-[var(--app-brand-line)] bg-white/80 p-4">
                              <div>
                                <h3 className="font-black text-[var(--app-brand-ink)]">Asignación masiva</h3>
                                <p className="text-sm text-[var(--app-brand-muted)]">Aplicá una plantilla a varias sucursales y días sin cerrar el calendario.</p>
                              </div>
                              <HeroSelectField value={bulkTemplateId || null} onChange={(value) => setBulkTemplateId(String(value ?? ''))} options={[{ id: '', label: 'Plantilla a aplicar' }, ...templates.map((template) => ({ id: template.id, label: templateLabel(template) }))]} />
                              <div className="grid gap-3 lg:grid-cols-2">
                                <div className="grid gap-2">
                                  <div className="text-xs font-black uppercase tracking-wide text-[var(--app-brand-muted)]">Sucursales</div>
                                  <div className="flex flex-wrap gap-2">
                                    {branches.map((branch) => (
                                      <label key={branch.id} className="flex cursor-pointer items-center gap-2 rounded-xl border border-[var(--app-brand-line)] bg-white px-3 py-2 text-sm font-semibold">
                                        <input type="checkbox" checked={bulkBranchIds.includes(branch.id)} onChange={() => toggleBulkBranch(branch.id)} />
                                        {branch.name}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div className="grid gap-2">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="text-xs font-black uppercase tracking-wide text-[var(--app-brand-muted)]">Días</div>
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="secondary" onPress={() => selectBulkDays(days.map((day) => day.id))}>Todos los días</Button>
                                      <Button size="sm" variant="secondary" onPress={() => selectBulkDays([1, 2, 3, 4, 5])}>Días laborales</Button>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {days.map((day) => (
                                      <label key={day.id} className="flex cursor-pointer items-center gap-2 rounded-xl border border-[var(--app-brand-line)] bg-white px-3 py-2 text-sm font-semibold">
                                        <input type="checkbox" checked={bulkDayIds.includes(day.id)} onChange={() => toggleBulkDay(day.id)} />
                                        {day.label}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button busy={bulkUpsertAssignments.isPending} disabled={!bulkTemplateId || bulkBranchIds.length === 0 || bulkDayIds.length === 0} onPress={applyBulkAssignments}>Aplicar al calendario</Button>
                              </div>
                            </section>
                            <div className="overflow-x-auto">
                              <table className="w-full min-w-[760px] text-sm">
                                <thead><tr><th className="p-2 text-left">Sucursal</th>{days.map((day) => <th key={day.id} className="p-2 text-left">{day.label}</th>)}</tr></thead>
                                <tbody>
                                  {branches.map((branch) => (
                                    <tr key={branch.id} className="border-t border-[var(--app-brand-line)]">
                                      <td className="p-2 font-bold">{branch.name}</td>
                                      {days.map((day) => {
                                        const assignment = assignments.find((item) => item.branchId === branch.id && item.dayOfWeek === day.id);
                                        return (
                                          <td key={day.id} className="p-2">
                                            <HeroSelectField className="min-w-36" value={assignment?.templateId || null} onChange={(value) => assign(branch.id, day.id, String(value ?? ''))} options={[{ id: '', label: 'Sin límite' }, ...templates.map((template) => ({ id: template.id, label: templateLabel(template) }))]} />
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Modal.Body>
                          <Modal.Footer>
                            <Button slot="close" variant="secondary">Cerrar</Button>
                          </Modal.Footer>
                        </Modal.Dialog>
                      </Modal.Container>
                    </Modal.Backdrop>
                  </Modal>
                </div>
              </div>
              <form onSubmit={addItem} className="mb-4 grid gap-3 rounded-2xl border border-[var(--app-brand-line)] bg-white/70 p-3 md:grid-cols-[1fr_140px_auto]">
                <HeroSelectField value={newItemProductId || null} onChange={(value) => setNewItemProductId(String(value ?? ''))} options={[{ id: '', label: 'Producto' }, ...products.map((product) => ({ id: product.id, label: product.name }))]} />
                <Input type="number" min={0} value={newItemStock} onChange={(event) => setNewItemStock(event.target.value)} />
                <Button type="submit" disabled={!newItemProductId} busy={updateItems.isPending}>Agregar</Button>
              </form>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-[var(--app-brand-muted)]"><tr><th className="py-2">Producto</th><th className="py-2">Stock diario</th></tr></thead>
                  <tbody>
                    {selectedTemplate.items.map((item) => (
                      <tr key={item.id} className="border-t border-[var(--app-brand-line)]">
                        <td className="py-3 font-semibold">{item.productName}</td>
                        <td className="py-3"><Input className="max-w-36" type="number" min={0} defaultValue={String(item.stockQuantity)} onBlur={(event) => updateItemStock(item.productId, event.target.value)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
}
